// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // 사용자 프로필 가져오기
  const fetchUserProfile = async (userId) => {
    try {
      
      // 사용자 프로필 조회
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(); // single() 대신 maybeSingle() 사용

      if (!data && !error) {
        // 프로필이 없으면 생성
        const { data: userData } = await supabase.auth.getUser();
        const newProfile = {
          user_id: userId,
          email: userData?.user?.email || '',
          display_name: userData?.user?.user_metadata?.display_name || userData?.user?.app_metadata?.login_id || userData?.user?.email?.split('@')[0] || '회원',
          created_at: new Date().toISOString()
        };


        const { data: createdProfile, error: insertError } = await supabase
          .from('user_profiles')
          .insert([newProfile])
          .select('*')
          .maybeSingle();

        if (insertError) {
          // 프로필 생성 실패해도 계속 진행
          return null;
        }

        return createdProfile;
      } else if (data) {
        return data;
      }
      
      return null;
    } catch (error) {
      // 에러가 발생해도 계속 진행
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    let isInitialized = false;
    
    // 즉시 세션 체크 (자동 로그인)
    const initializeAuth = async () => {
      try {
        
        // 오프라인 체크
        if (!navigator.onLine) {
          if (mounted) {
            setAuthLoading(false);
          }
          return;
        }
        
        // getSession은 로컬 스토리지에서 세션을 가져오므로 빠름
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          if (mounted) {
            setAuthLoading(false);
          }
          return;
        }
        
        if (session?.user && !isInitialized) {
          isInitialized = true;
          if (mounted) {
            setUser(session.user);
            // 프로필 가져오기는 비동기로 처리, 실패해도 계속 진행
            fetchUserProfile(session.user.id)
              .then(profile => {
                if (mounted) {
                  setUserProfile(profile);
                }
              })
              .catch(err => {
                // 프로필 실패해도 사용자는 유지
              });
          }
        } else if (!session) {
        }
      } catch (error) {
      } finally {
        // 항상 로딩 종료
        if (mounted) {
          // 약간의 지연 후 로딩 종료 (네트워크 안정화)
          setTimeout(() => {
            if (mounted) {
              setAuthLoading(false);
            }
          }, 500);
        }
      }
    };

    // 즉시 실행
    initializeAuth();

    // 온라인/오프라인 이벤트 리스너
    const handleOnline = () => {
      initializeAuth();
    };

    const handleOffline = () => {
      if (mounted) {
        setAuthLoading(false);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 인증 상태 변경 리스너 설정
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;


        // 초기화가 이미 완료된 경우 INITIAL_SESSION 이벤트 무시
        if (event === 'INITIAL_SESSION' && isInitialized) {
          return;
        }

        if (event === 'SIGNED_IN' && session?.user) {
          if (!isInitialized || user?.id !== session.user.id) {
            setUser(session.user);
            isInitialized = true;
            fetchUserProfile(session.user.id)
              .then(profile => {
                if (mounted) {
                  setUserProfile(profile);
                }
              })
              .catch(err => {
              });
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setUserProfile(null);
          isInitialized = false;
        } else if ((event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') && session?.user) {
          setUser(session.user);
          // 토큰 갱신 시에는 프로필 재조회 불필요
        }

        // 인증 상태 변경 시 로딩 종료
        if (mounted) {
          setAuthLoading(false);
        }
      }
    );

    // Cleanup
    return () => {
      mounted = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 빈 dependency로 한 번만 실행

  // 이메일/비밀번호로 로그인
  const signInWithEmail = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      // 로그인 성공 시 프로필 가져오기
      if (data?.user) {
        const profile = await fetchUserProfile(data.user.id);
        setUserProfile(profile);
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // 이메일/비밀번호로 회원가입 (관리자용)
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName
          }
        }
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // 비밀번호 재설정 이메일 전송
  const resetPassword = async (email) => {
    try {
      // 고정 사이트 URL 사용 (환경변수 우선, fallback으로 현재 origin)
      const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteUrl}/reset-password`,
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // 비밀번호 변경
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      // 세션이 없어도 로컬 상태는 정리
      setUser(null);
      setUserProfile(null);
      
      // Supabase 로그아웃 시도 (실패해도 로컬 상태 정리는 완료됨)
      try {
        const { error } = await supabase.auth.signOut();
        if (error && error.message !== 'Auth session missing!') {
          console.warn('Logout warning:', error.message);
        }
      } catch (logoutError) {
        // 세션이 이미 없거나 만료된 경우는 무시
        if (logoutError.message && !logoutError.message.includes('Auth session missing')) {
          console.warn('Logout error (ignored):', logoutError.message);
        }
      }
    } catch (error) {
      // 최종 fallback - 로컬 상태만 정리하고 에러는 무시
      setUser(null);
      setUserProfile(null);
      console.warn('Fallback logout:', error.message);
    }
  };

  const linkPlayerToUser = async (playerId) => {
    if (!user) return;

    try {
      // 선수 정보 조회 — 이미 다른 유저에게 연결된 선수인지 확인
      const { data: playerData, error: fetchError } = await supabase
        .from('players')
        .select('id, user_id')
        .eq('id', playerId)
        .single();

      if (fetchError) throw fetchError;

      if (playerData) {
        // 이미 다른 유저에게 연결된 선수는 탈취 방지
        if (playerData.user_id && playerData.user_id !== user.id) {
          throw new Error('이미 다른 사용자에게 연결된 선수입니다.');
        }

        // user_id가 없는 선수만 연결 (is null 조건 추가)
        const { data: updatedPlayer, error: playerError } = await supabase
          .from('players')
          .update({ user_id: user.id })
          .eq('id', playerId)
          .is('user_id', null)
          .select('id');

        if (playerError) throw playerError;

        // 동시 요청으로 인해 다른 유저가 먼저 연결한 경우
        if (!updatedPlayer || updatedPlayer.length === 0) {
          throw new Error('이미 다른 사용자에게 연결된 선수입니다.');
        }
      }

      // 사용자 프로필에 player_id 연결
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ player_id: playerId })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // 프로필 새로고침
      const profile = await fetchUserProfile(user.id);
      setUserProfile(profile);
    } catch (error) {
      throw error;
    }
  };

  const unlinkPlayerFromUser = async () => {
    if (!user || !userProfile?.player_id) return;
    
    try {
      const playerId = userProfile.player_id;
      
      // 선수 정보에서 user_id 제거
      const { error: playerError } = await supabase
        .from('players')
        .update({ user_id: null })
        .eq('id', playerId);

      if (playerError) throw playerError;

      // 사용자 프로필에서 player_id 제거
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ player_id: null })
        .eq('user_id', user.id);

      if (profileError) throw profileError;

      // 프로필 새로고침
      const profile = await fetchUserProfile(user.id);
      setUserProfile(profile);
    } catch (error) {
      throw error;
    }
  };

      const value = {
        user,
        userProfile,
        loading: authLoading,
        signInWithEmail,
        signUpWithEmail,
        resetPassword,
        updatePassword,
        signOut,
        linkPlayerToUser,
        unlinkPlayerFromUser,
      };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
